import React from 'react';
import { onlineManager, useQuery } from '@tanstack/react-query';
import { getUserService } from '../../../service/ServiceRegistry';
import {
  formatDateForDisplay,
  formatTimeForDisplay,
} from '../../../types/api/admin-user';
import type { AdminUser, UserTier } from '../../../types/api/admin-user';

// ────────────────────────── Visual helpers ─────────────────────────

const TIER_CONFIG: Record<UserTier, { color: string; icon: string }> = {
  FREE: { color: 'bg-gray-100 text-gray-800', icon: '🆓' },
  PREMIUM: { color: 'bg-yellow-100 text-yellow-800', icon: '⭐' },
  VIP: { color: 'bg-purple-100 text-purple-800', icon: '👑' },
};

const GENDER_COLORS: Record<string, string> = {
  MALE: 'bg-blue-100 text-blue-800',
  FEMALE: 'bg-pink-100 text-pink-800',
  OTHER: 'bg-purple-100 text-purple-800',
  PREFER_NOT_TO_SAY: 'bg-gray-100 text-gray-800',
};

function formatGender(gender: string | null | undefined): string {
  if (!gender) return '-';
  return gender.charAt(0) + gender.slice(1).toLowerCase().replace('_', ' ');
}

function lastLoginRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(iso)
    ? iso + 'Z'
    : iso;
  const loginDate = new Date(normalized);
  if (Number.isNaN(loginDate.getTime())) return 'Never';
  const days = Math.floor((Date.now() - loginDate.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

// ────────────────────────── Component ─────────────────────────────

const RECENT_USERS_LIMIT = 10;

export interface RecentUsersListProps {
  /** How many newest users to display (default: 10). */
  limit?: number;
}

/**
 * RecentUsersList — Latest sign-ups rendered with the same table layout
 * as the User Management page so navigating between the two pages feels
 * continuous. Backed by `/admin/users` (no dedicated analytics endpoint
 * required), polling every 30s.
 */
export const RecentUsersList: React.FC<RecentUsersListProps> = ({
  limit = RECENT_USERS_LIMIT,
}) => {
  const query = useQuery({
    queryKey: ['analytics', 'recent-users', limit] as const,
    queryFn: async () => {
      const service = getUserService();
      const data = await service.getAdminUsers({ size: limit, page: 1 });
      return data.items;
    },
    enabled: onlineManager.isOnline(),
    refetchInterval: 30_000,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
  });

  // Sort by registrationDate desc client-side as a safety net in case the
  // backend doesn't guarantee newest-first ordering on /admin/users.
  const items: AdminUser[] = (query.data ?? [])
    .slice()
    .sort((a, b) => (b.registrationDate ?? '').localeCompare(a.registrationDate ?? ''))
    .slice(0, limit);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
        <div className="text-left">
          <h4 className="text-sm font-semibold text-gray-700">Recent Users</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Showing the last {limit} sign-ups
          </p>
        </div>
        <a
          href="/admin/users"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 transition-colors"
        >
          View all
        </a>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                style={{ minWidth: '250px' }}
              >
                User
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Phone
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Gender
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Tier
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Registered
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Last Login
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {query.isLoading && items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-10 text-center text-sm text-gray-400"
                >
                  Loading users…
                </td>
              </tr>
            ) : query.error ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-10 text-center text-sm text-red-600"
                >
                  Failed to load recent users.
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-10 text-center text-sm text-gray-400"
                >
                  No users yet.
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const tier = TIER_CONFIG[row.tier] ?? TIER_CONFIG.FREE;
                return (
                  <tr
                    key={row.userId}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* User: avatar + name + email */}
                    <td
                      className="px-6 py-4 text-sm text-gray-900 text-left"
                      style={{ minWidth: '250px' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#EAB308] to-[#CA8A04] flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {(row.name ?? '').charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                          <p className="text-xs text-gray-500">{row.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Phone */}
                    <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-900 text-left">
                      <span className="text-sm text-gray-900">{row.phone || '-'}</span>
                    </td>

                    {/* Gender */}
                    <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-900 text-left">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          GENDER_COLORS[row.gender ?? ''] ?? 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {formatGender(row.gender)}
                      </span>
                    </td>

                    {/* Tier */}
                    <td className="px-6 py-4 text-sm text-gray-900 text-left">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${tier.color}`}
                      >
                        <span>{tier.icon}</span>
                        <span>{row.tier}</span>
                      </span>
                    </td>

                    {/* Registered */}
                    <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-900 text-left">
                      <div>
                        <p className="text-sm text-gray-900">
                          {formatDateForDisplay(row.registrationDate)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTimeForDisplay(row.registrationDate)}
                        </p>
                      </div>
                    </td>

                    {/* Last Login */}
                    <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-900 text-left">
                      {row.lastLogin ? (
                        <div>
                          <p className="text-sm text-gray-900">
                            {formatDateForDisplay(row.lastLogin)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {lastLoginRelative(row.lastLogin)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Never</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentUsersList;
